import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  addDriver,
  bulkInsertDriver,
  deleteDriver,
  updateDriver,
} from "redux/actions/driver";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import DriverRes from "types/res/driver/DriverRes";
import { FormType } from "components/FormField";
import CreateDriverReq from "types/req/driver/CreateDriverReq";
import UpdateDriverReq from "types/req/driver/UpdateDriverReq";
import CsvImport from "components/CsvImport";
import { Box, Button, TextField } from "@material-ui/core";
import { CsvFieldType } from "utils/CsvUtils";
import EnvironmentUtils from "utils/EnviromentUtils";
import DriverApi from "api/DriverApi";

const Driver: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  const [driver, setDriver] = useState<DriverRes[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const loadDrivers = useCallback(
    async (opts: { limit: number; offset: number; name: string }) => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const [list, cnt] = await Promise.all([
          DriverApi.findAllWithPagination(companyId, {
            limit: opts.limit,
            offset: opts.offset,
            name: opts.name || undefined,
          }),
          DriverApi.count(companyId, { name: opts.name || undefined }),
        ]);
        setDriver(list);
        setTotalCount(cnt);
      } finally {
        setIsLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    loadDrivers({ limit, offset: offset * limit, name: searchName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, limit, offset]);

  const onSearch = () => {
    setOffset(0);
    loadDrivers({ limit, offset: 0, name: searchName });
  };

  const headers: { key: keyof DriverRes; label: string }[] = [
    { key: "name", label: t("driver.headers.name") },
    { key: "email", label: t("driver.headers.email") },
    { key: "address", label: t("driver.headers.address") },
    { key: "tel", label: t("driver.headers.tel") },
    { key: "carType", label: t("driver.headers.carType") },
    { key: "carColor", label: t("driver.headers.carColor") },
    { key: "carNumber", label: t("driver.headers.carNumber") },
    { key: "remarks", label: t("driver.headers.remarks") },
    { key: "sort", label: t("driver.headers.sort") },
    { key: "lineUserId" as any, label: t("driver.headers.lineUserId") },
    { key: "notifyMethod" as any, label: t("driver.headers.notifyMethod") },
  ];

  const forms = [
    {
      label: t("driver.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.email"),
      key: "email",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.password"),
      key: "password",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.address"),
      key: "address",
      type: FormType.TextArea,
    },
    {
      label: t("driver.forms.tel"),
      key: "tel",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.carType"),
      key: "carType",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.carColor"),
      key: "carColor",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.carNumber"),
      key: "carNumber",
      type: FormType.Text,
    },
    {
      label: t("driver.forms.remarks"),
      key: "remarks",
      type: FormType.TextArea,
    },
    {
      label: t("driver.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    // LINE 連携はドライバー本人がモバイル画面から行います。
    // ここで lineUserId を手入力するフォームは廃止しました。
  ];

  const csvHeader = [
    {
      label: "名前",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "メールアドレス",
      key: "email",
      type: CsvFieldType.Text,
    },
    {
      label: "初期パスワード",
      key: "password",
      type: CsvFieldType.Text,
    },
    {
      label: "住所",
      key: "address",
      type: CsvFieldType.Text,
    },
    {
      label: "電話番号",
      key: "tel",
      type: CsvFieldType.Text,
    },
    {
      label: "車種",
      key: "carType",
      type: CsvFieldType.Text,
    },
    {
      label: "車色",
      key: "carColor",
      type: CsvFieldType.Text,
    },
    {
      label: "車ナンバー",
      key: "carNumber",
      type: CsvFieldType.Text,
    },
    {
      label: "備考",
      key: "remarks",
      type: CsvFieldType.Text,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={1}
        marginX={2}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            label={t("driver.search.nameLabel")}
            size="small"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onSearch}
            disabled={isLoading}
          >
            {t("driver.search.searchButton")}
          </Button>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.open(EnvironmentUtils.getDriverDomain())}
          >
            {t("driver.mobileButton")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCsvImport((prev) => !prev)}
          >
            {t("driver.csvImport")}
          </Button>
        </Box>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/driver.csv"}
          templateName={"driver_template.csv"}
          csvHeader={csvHeader}
          addType={CreateDriverReq}
          addFunc={(formData) => bulkInsertDriver(companyId, formData)}
        />
      )}
      <DriverTable
        title={t("driver.title")}
        formId="driverForm"
        forms={forms}
        rows={headers}
        data={driver}
        isLoading={isLoading}
        totalCount={totalCount}
        limit={limit}
        offset={offset}
        setLimit={setLimit}
        setOffset={(v: any) => setOffset(Number(v))}
        addFunc={(formData: any) => async (d: any) => {
          const r = await addDriver(companyId, formData)(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        updateFunc={(formData: any) => async (d: any) => {
          const r = await updateDriver(companyId, formData)(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        deleteFunc={(item: any) => async (d: any) => {
          const r = await deleteDriver(companyId, {
            driverId: item.driverId,
          })(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        addType={CreateDriverReq}
        updateType={UpdateDriverReq}
        values={[
          (s) => s.name,
          (s) => s.email,
          (s) => s.address,
          (s) => s.tel,
          (s) => s.carType,
          (s) => s.carColor,
          (s) => s.carNumber,
          (s) => s.remarks,
          (s) => s.sort || t("driver.values.unset"),
          (s: any) => s.lineUserId ? t("driver.values.linked") : t("driver.values.notLinked"),
          (s: any) => {
            const methods: any = {
              email: t("driver.notifyMethod.email"),
              line: t("driver.notifyMethod.line"),
              both: t("driver.notifyMethod.both"),
            };
            return methods[s.notifyMethod] || t("driver.notifyMethod.email");
          },
        ]}
      />
    </>
  );
};

const DriverTable = styled<
  React.FC<CommonTableProps<DriverRes, CreateDriverReq, UpdateDriverReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Driver;
